import { ReactElement } from 'react';

import { NamePath } from 'antd/es/form/interface';

import SecureFormItem from '@/components/form/form-items/SecureFormItem';

const ENVIRONMENT = process.env.ENVIRONMENT || '';

const testSsnCodes = ['666316020', '666066243', '000000000'];

type TSSNFormItemProps = {
	name: NamePath;
	fullName?: NamePath;
	required: boolean;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export const SSNFormItem = ({
	name,
	fullName,
	required,
	onBlur,
}: TSSNFormItemProps): ReactElement => {
	const formatValue = (value: string, partFormat = (part: string) => part): string => {
		if (!value) return '';

		const sanitized = value.replace(/[^\d-]/g, '');
		const digits = sanitized.replace(/-/g, '');

		const formatted = digits.replace(/^(\d{0,3})(\d{0,2})(\d{0,4})$/, (_match, p1, p2, p3) => {
			let res = partFormat(p1);

			if (p2) res += `-${partFormat(p2)}`;
			if (p3) res += `-${partFormat(p3)}`;

			return res;
		});

		// check if there is last char - "-" and if it is a suitable place for it
		if (sanitized.endsWith('-') && [3, 5].some((length) => digits.length === length)) {
			return formatted + '-';
		}

		return formatted;
	};

	const validator = (value: string): Promise<void> => {
		if (!value) {
			return Promise.resolve();
		}

		const rawValue = value.replace(/-/g, '');

		if (['dev', 'local-dev'].includes(ENVIRONMENT) && testSsnCodes.includes(rawValue)) {
			return Promise.resolve();
		}

		const isValidValue = /^\d{3}-\d{2}-\d{4}$/.test(value);
		const isValidRawValue =
			/^(?!(\d)\1{8})(?!123456789)(?!078051120)(?!219099999)(?!666|000|9\d{2})\d{3}(?!00)\d{2}(?!0{4})\d{4}$/.test(
				rawValue,
			);

		return isValidRawValue && isValidValue
			? Promise.resolve()
			: Promise.reject(new Error('Please enter a valid Social Security Number'));
	};

	return (
		<SecureFormItem
			formatRawValue={formatValue}
			fullName={fullName}
			inputMode="numeric"
			label="Social Security Number"
			maxLength={11}
			name={name}
			placeholder="Enter Social Security Number"
			rules={[{ required }]}
			validator={validator}
			onBlur={onBlur}
		/>
	);
};
